import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, Paper, CircularProgress } from '@mui/material';
import { SupplierService } from '../../services/supplier.service';
import { AuthService } from '../../services/auth.service';
import { SupplierCreateDTO } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';

export const SupplierForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const editing = Boolean(id);
  const [form, setForm] = useState<SupplierCreateDTO>({
    name: '', contactPerson: '', email: '', phone: '', address: '', taxRegistrationNo: '',
    institutionId: AuthService.getSelectedInstitution()!!,
  });
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    SupplierService.getById(id!).then(s => {
      setForm({
        name: s.name,
        contactPerson: s.contactPerson ?? '',
        email: s.email ?? '',
        phone: s.phone ?? '',
        address: s.address ?? '',
        taxRegistrationNo: s.taxRegistrationNo ?? '',
        institutionId: s.institutionId,
      });
    }).catch(e => toast.showApiError(e, 'Failed to load supplier'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleField = (k: keyof SupplierCreateDTO) =>
    (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.showApiError(null, 'Name is required'); return; }
    try {
      setSaving(true);
      if (editing) await SupplierService.update(id!, form);
      else await SupplierService.create(form);
      toast.showSuccess(`Supplier ${editing ? 'updated' : 'created'}`);
      navigate('/procurement/suppliers');
    } catch (e) {
      toast.showApiError(e, `Failed to ${editing ? 'update' : 'create'} supplier`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 720 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>{editing ? 'Edit Supplier' : 'New Supplier'}</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField label="Name" required value={form.name} onChange={handleField('name')} inputProps={{ 'aria-label': 'Name' }} />
        <TextField label="Contact Person" value={form.contactPerson} onChange={handleField('contactPerson')} />
        <TextField label="Email" type="email" value={form.email} onChange={handleField('email')} />
        <TextField label="Phone" value={form.phone} onChange={handleField('phone')} />
        <TextField label="Address" multiline rows={2} value={form.address} onChange={handleField('address')} />
        <TextField label="Tax Registration No" value={form.taxRegistrationNo} onChange={handleField('taxRegistrationNo')} />
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={saving}>Save</Button>
        </Box>
      </Paper>
    </Box>
  );
};
